/**
 * §P.1 · Ficha médica y contactos de emergencia.
 *
 * **Categoría especial de datos** (RGPD art. 9). Quien la ve es quien puede
 * necesitarla con un niño delante: el entrenador sí, el asistente no.
 */

export interface FichaMedica {
  readonly allergies: string | null;
  readonly conditions: string | null;
  readonly medication: string | null;
  readonly blood_type: string | null;
  readonly dietary_notes: string | null;
  readonly doctor_phone: string | null;
  /**
   * Quién la tocó por última vez.
   *
   * **La editan la familia y el club**, así que cuando algo está mal la primera
   * pregunta es quién lo escribió. Por eso se enseña y no se esconde.
   */
  readonly updated_by: string | null;
  readonly updated_by_name: string | null;
  readonly updated_at: Date | string | null;
}

/** Un contacto de emergencia. El orden de la lista **es** la prioridad. */
export interface ContactoDeEmergencia {
  readonly id?: string | undefined;
  readonly full_name: string;
  readonly phone: string;
  readonly relationship: string | null;
  readonly priority?: number | undefined;
}

export const MAXIMO_DE_CONTACTOS = 10;

/** ¿Hay algo escrito en la ficha? Una vacía y una que no existe son lo mismo. */
export const estaVacia = (f: FichaMedica): boolean =>
  !f.allergies &&
  !f.conditions &&
  !f.medication &&
  !f.blood_type &&
  !f.dietary_notes &&
  !f.doctor_phone;

/* ── §P.1 fase 2 · Lesiones ──────────────────────────────────────────────── */

/**
 * Una lesión de un alumno.
 *
 * **`active` es lo que el maestro mira antes de montar la clase del martes.** Y es la
 * razón de que `return_expected_on` no cierre nada sola: una previsión no es un alta,
 * y darla por buena devolvería a la actividad a alguien que nadie ha revisado.
 */
export interface Lesion {
  readonly id: string;
  readonly student_id: string;
  readonly occurred_on: Date | string;
  readonly description: string;
  readonly severity: string | null;
  /** Una **previsión**. No cierra la lesión. */
  readonly return_expected_on: Date | string | null;
  /** El alta médica. Con fecha, cierra; a `null`, reabre. */
  readonly returned_on: Date | string | null;
  /** Sin alta médica. Ver la cabecera. */
  readonly active: boolean;
  readonly recorded_by: string | null;
  readonly created_at: Date | string;
}

export type Gravedad = 'leve' | 'moderada' | 'grave';

export const GRAVEDADES: readonly {
  valor: Gravedad;
  etiqueta: string;
  clase: string;
}[] = [
  { valor: 'leve', etiqueta: 'Leve', clase: 'pastilla' },
  { valor: 'moderada', etiqueta: 'Moderada', clase: 'pastilla-paused' },
  { valor: 'grave', etiqueta: 'Grave', clase: 'pastilla-mal' },
];

export const pintaDeGravedad = (
  severity: string | null
): { etiqueta: string; clase: string } | null =>
  GRAVEDADES.find((g) => g.valor === severity) ??
  (severity ? { etiqueta: severity, clase: 'pastilla' } : null);

/**
 * Si la vuelta prevista ya pasó y sigue sin alta.
 *
 * Es el caso que hay que enseñar: la previsión decía que volvía el día 10, estamos a
 * 17 y **nadie le ha dado el alta**. No significa que esté peor; significa que hay una
 * conversación pendiente con la familia, y que nadie la va a tener si la pantalla
 * enseña la fecha prevista como si todavía valiera.
 */
export function vueltaVencida(l: Lesion, hoy = new Date()): boolean {
  if (!l.active || l.return_expected_on === null) return false;
  const prevista = new Date(l.return_expected_on);
  if (Number.isNaN(prevista.getTime())) return false;
  // Se compara por día, no por instante: a las 23:00 del día 10 no está vencida.
  return prevista.toISOString().slice(0, 10) < hoy.toISOString().slice(0, 10);
}
